import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminAppBody from "../../../../components/admin/AdminAppBody";
import useApiCallHooks from "../../../../hooks/useApiCallHooks";
import { toast } from "react-toastify";

export default function ShowAttendance() {
  const navigate = useNavigate();

  // API hook
  const [response, loading, error, callAPI, statusCode] = useApiCallHooks();
  const [attendanceData, setAttendanceData] = useState([]);

  // Filters
  const [filters, setFilters] = useState({
    from_date: "",
    to_date: "",
  });

  // Modal states
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Fetch initial attendance
  useEffect(() => {
    fetchAttendance();
  }, []);

  const fetchAttendance = () => {
    let query = "";
    if (filters.from_date && filters.to_date) {
      query = `?from_date=${filters.from_date}&to_date=${filters.to_date}`;
    }
    callAPI("get", `admin/get-attendance${query}`);
  };

  // Handle API response
  useEffect(() => {
    if (response?.data && statusCode === 200) {
      setAttendanceData(response.data);
    }
  }, [response, statusCode]);

  // Compute total hours
  const getTotalHours = (in_time, out_time) => {
    if (!in_time || !out_time) return "-";
    const [ih, im] = in_time.split(":").map(Number);
    const [oh, om] = out_time.split(":").map(Number);
    const inDate = new Date(0, 0, 0, ih, im);
    const outDate = new Date(0, 0, 0, oh, om);
    const diff = (outDate - inDate) / 60000;
    if (diff <= 0) return "-";
    return `${Math.floor(diff / 60)}h ${diff % 60}m`;
  };

  // Open modal
  const handleViewClick = (item) => {
    setSelectedItem({ ...item });
    setShowModal(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedItem(null);
  };

  // Handle filter input change
  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
  };

  // Apply filters
  const handleSearch = () => {
    if (!filters.from_date || !filters.to_date) {
      toast.error("Please select both From and To dates");
      return;
    }
    fetchAttendance();
  };

  // Clear filters
  const handleClear = () => {
    setFilters({ from_date: "", to_date: "" });
    fetchAttendance();
  };

  return (
    <AdminAppBody
      loading={loading}
      content={
        <div className="container py-3">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0">Attendance List</h4>
          </div>

          {/* Filters */}
          <div className="card mb-3">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-md-4">
                  <label className="form-label">From Date</label>
                  <input
                    type="date"
                    name="from_date"
                    value={filters.from_date}
                    onChange={handleFilterChange}
                    className="form-control"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">To Date</label>
                  <input
                    type="date"
                    name="to_date"
                    value={filters.to_date}
                    onChange={handleFilterChange}
                    className="form-control"
                  />
                </div>
                <div className="col-md-2">
                  <button
                    className="btn btn-primary w-100"
                    onClick={handleSearch}
                    disabled={loading}
                  >
                    {loading ? "Loading..." : "Search"}
                  </button>
                </div>
                <div className="col-md-2">
                  <button
                    className="btn btn-danger w-100"
                    onClick={handleClear}
                    disabled={loading}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="card shadow-sm">
            <div className="card-body p-0">
              {loading ? (
                <div className="p-3 text-center">Loading...</div>
              ) : attendanceData.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-striped table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Sr.no</th>
                        <th>User Name</th>
                        <th>Date</th>
                        <th>In Time</th>
                        <th>Out Time</th>
                        <th>Working Hours</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendanceData.map((item, i) => (
                        <tr key={item.id}>
                          <td>{i + 1}</td>
                          <td>
                            {item.user.first_name} {item.user.last_name}
                          </td>
                          <td>{item.date}</td>
                          <td>{item.in_time}</td>
                          <td>{item.out_time || "-"}</td>
                          <td>{getTotalHours(item.in_time, item.out_time)}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-info"
                              onClick={() => handleViewClick(item)}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-3 text-center">
                  No attendance records found.
                </div>
              )}
            </div>
          </div>

          {/* Modal */}
          {showModal && selectedItem && (
            <div
              className="modal fade show"
              style={{
                display: "block",
                backgroundColor: "rgba(0,0,0,0.5)",
              }}
            >
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Task Summary</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={handleCloseModal}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <p>
                      <strong>Date:</strong> {selectedItem.date}
                    </p>
                    <p>
                      <strong>User:</strong>{" "}
                      {selectedItem.user.first_name}{" "}
                      {selectedItem.user.last_name}
                    </p>
                    <div className="mb-3">
                      <label className="form-label">Task Summary</label>
                      <textarea
                        className="form-control"
                        rows="5"
                        readOnly
                        value={selectedItem.task_summary || "No summary provided"}
                      ></textarea>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCloseModal}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      }
    />
  );
}
